import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/i18n";

const useIsWide = () => {
  const q = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
};

const NavBtn = ({ dir, onClick, disabled }) => {
  const { t } = useLang();
  return (
  <button type="button" onClick={onClick} disabled={disabled} className={`bk-nav ${dir === "prev" ? "-left-2 lg:-left-6" : "-right-2 lg:-right-6"}`} aria-label={t(dir === "prev" ? "book.prev" : "book.next")} data-testid={`book-${dir}-btn`}>
    {dir === "prev" ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
  </button>
  );
};

const Leaf = ({ dir, front, back, onDone }) => (
  <motion.div
    className={`bk-leaf ${dir === "next" ? "bk-leaf-right" : "bk-leaf-left"}`}
    initial={{ rotateY: 0 }}
    animate={{ rotateY: dir === "next" ? -180 : 180 }}
    transition={{ duration: 0.75, ease: [0.45, 0.05, 0.25, 1] }}
    onAnimationComplete={onDone}
  >
    <div className="bk-face">{front}</div>
    <div className="bk-face bk-face-back">{back}</div>
  </motion.div>
);

const useKeys = (prev, next) => {
  useEffect(() => {
    const on = (e) => {
      if (e.target.closest?.("input, textarea, [role=dialog]")) return;
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [prev, next]);
};

const Spread = ({ count, renderPage }) => {
  const { t } = useLang();
  const [s, setS] = useState(0);
  const [flip, setFlip] = useState(null);
  const last = Math.ceil(count / 2) - 1;
  const next = () => { if (!flip && s < last) setFlip("next"); };
  const prev = () => { if (!flip && s > 0) setFlip("prev"); };
  useKeys(prev, next);
  const done = () => { setS((v) => v + (flip === "next" ? 1 : -1)); setFlip(null); };
  const left = flip === "prev" ? 2 * s - 2 : 2 * s;
  const right = flip === "next" ? 2 * s + 3 : 2 * s + 1;
  return (
    <div className="relative mx-auto" style={{ width: "min(100%, calc((100vh - 200px) * 1.55))" }}>
      <div className="bk-spread" data-testid="book-spread">
        <div className="bk-half">{renderPage(left)}</div>
        <div className="bk-half">{renderPage(right)}</div>
        <span className="bk-gutter" />
        {flip === "next" && <Leaf key={`n${s}`} dir="next" front={renderPage(2 * s + 1)} back={renderPage(2 * s + 2)} onDone={done} />}
        {flip === "prev" && <Leaf key={`p${s}`} dir="prev" front={renderPage(2 * s)} back={renderPage(2 * s - 1)} onDone={done} />}
      </div>
      <NavBtn dir="prev" onClick={prev} disabled={s === 0} />
      <NavBtn dir="next" onClick={next} disabled={s >= last} />
      <p className="mono mt-4 text-center text-[11px] text-[var(--muted)]" data-testid="book-page-indicator">{t("book.pages", { a: 2 * s + 1, b: Math.min(2 * s + 2, count), n: count })}</p>
    </div>
  );
};

const Single = ({ count, renderPage }) => {
  const { t } = useLang();
  const [p, setP] = useState(0);
  const [dir, setDir] = useState(1);
  const go = (d) => { const n = p + d; if (n >= 0 && n < count) { setDir(d); setP(n); } };
  const prev = () => go(-1);
  const next = () => go(1);
  useKeys(prev, next);
  return (
    <div className="relative mx-auto max-w-[520px]">
      <div className="bk-single" data-testid="book-single">
        <motion.div key={p} className="h-full" initial={{ rotateY: dir * -35, opacity: 0.3 }} animate={{ rotateY: 0, opacity: 1 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} style={{ transformOrigin: dir > 0 ? "left center" : "right center" }}>
          {renderPage(p)}
        </motion.div>
      </div>
      <NavBtn dir="prev" onClick={prev} disabled={p === 0} />
      <NavBtn dir="next" onClick={next} disabled={p >= count - 1} />
      <p className="mono mt-4 text-center text-[11px] text-[var(--muted)]" data-testid="book-page-indicator">{t("book.page", { a: p + 1, n: count })}</p>
    </div>
  );
};

export const Flipbook = ({ count, renderPage }) => {
  const wide = useIsWide();
  return wide ? <Spread count={count} renderPage={renderPage} /> : <Single count={count} renderPage={renderPage} />;
};
